/**
 * Single-producer/single-consumer push queue exposed as an
 * `AsyncIterable` — the consolidated streaming primitive for the SDK
 * (P0b of `docs/cloud-vm/design/crucible-client-sdk-tdd.md`, resolving
 * the doc's open question 4).
 *
 * Both wire clients re-implemented this independently:
 *   - the browser `BrowserCrucibleClient`'s `makeAsyncQueue` — had BOTH
 *     `close()` (clean completion) and `fail(err)` (throw in the iterator);
 *   - the node `JsonRpcClient.stream`'s inline queue — had only the
 *     completion path (`finishQueue()`), surfacing stream errors via the
 *     `done` promise rejecting while the chunk iterable just *completed*.
 *
 * The browser version is the strict superset, so it is canonical here. The
 * **error-termination choice is the consumer's**, not the queue's: a stream
 * implementation that wants the browser contract (iterating throws on error)
 * calls `fail(err)`; one that wants the node contract (chunks complete; the
 * error rides the `done` promise) calls `close()` and rejects `done`
 * separately. Keeping both modes means re-seating either client onto this
 * queue is behaviour-preserving.
 *
 * Contract:
 *   - `push(chunk)` — enqueue; delivers immediately to a waiting consumer,
 *     else buffers (FIFO). No-op once closed or failed.
 *   - `close()` — end cleanly; a waiting `next()` resolves `{done:true}`, and
 *     subsequent `next()` calls do too (after any buffered chunks drain).
 *   - `fail(err)` — end abnormally; a waiting `next()` rejects with `err`, and
 *     subsequent `next()` calls re-throw it (after any buffered chunks drain).
 *   - Single-pass: each chunk delivered exactly once, in FIFO order. Buffered
 *     chunks always drain BEFORE the terminal signal (done/throw) is observed.
 *   - `close()` / `fail()` are idempotent; the first terminal call wins.
 */
export interface AsyncQueue<T> {
  readonly iterable: AsyncIterable<T>;
  push(chunk: T): void;
  close(): void;
  fail(err: Error): void;
}

export function makeAsyncQueue<T>(): AsyncQueue<T> {
  const buffer: T[] = [];
  let waiting: {
    resolve: (r: IteratorResult<T>) => void;
    reject: (err: Error) => void;
  } | null = null;
  let closed = false;
  let failure: Error | null = null;

  const push = (chunk: T): void => {
    if (closed || failure) return;
    if (waiting) {
      const w = waiting;
      waiting = null;
      w.resolve({ value: chunk, done: false });
      return;
    }
    buffer.push(chunk);
  };

  const close = (): void => {
    if (closed || failure) return;
    closed = true;
    if (waiting) {
      const w = waiting;
      waiting = null;
      w.resolve({ value: undefined as unknown as T, done: true });
    }
  };

  const fail = (err: Error): void => {
    if (closed || failure) return;
    failure = err;
    if (waiting) {
      const w = waiting;
      waiting = null;
      w.reject(err);
    }
  };

  const iterable: AsyncIterable<T> = {
    [Symbol.asyncIterator](): AsyncIterator<T> {
      return {
        next(): Promise<IteratorResult<T>> {
          // Buffered chunks always drain first — even after the terminal
          // signal — so a fast producer that pushed-then-closed still
          // yields every chunk before the consumer observes the end.
          if (buffer.length > 0) {
            return Promise.resolve({ value: buffer.shift()!, done: false });
          }
          if (failure) return Promise.reject(failure);
          if (closed) {
            return Promise.resolve({ value: undefined as unknown as T, done: true });
          }
          return new Promise<IteratorResult<T>>((resolve, reject) => {
            waiting = { resolve, reject };
          });
        },
      };
    },
  };

  return { iterable, push, close, fail };
}
